import { useQuery } from "@apollo/client";
import * as Yup from "yup";
import { FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY } from "../../../api/person/queries";
import { FuldmaegtigeForPersonResponse } from "../../../components/autocomplete/interface";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";
import { getNumberOfFuldmaegtigeAtDate } from "../../../util/person";
import { AlertMessage } from "../../../components/AlertMessage";

export const personPrincipalPersonFuldmForm = (
  personId: string,
  personPrincipalObjId?: string,
): FormConfig<PersonPrincipalPersonFuldmDTO> => {
  return {
    lockedValues: [
      {
        id: "personId",
        value: personId,
      },
      {
        id: "personPrincipalObjId",
        value: personPrincipalObjId,
      },
    ],
    title: "personrelation",
    validationSchema: Yup.object({
      personPrincipalId: Yup.string().required("Principal skal være valgt"),
      periodeFraDato: Yup.date().nullable().required("Startdato skal være sat"),
      periodeTilDatoInklusiv: Yup.date()
        .nullable()
        .when(
          "periodeFraDato",
          (periodeFraDato, yup) =>
            periodeFraDato &&
            yup.min(periodeFraDato, "Slutdato kan ikke være inden startdato"),
        ),
    }),
    form: [
      {
        component: "person",
        erUdoevendeAdvokat: true,
        id: "personPrincipalId",
        label: "Principal",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato",
        initialValue: "",
      },
      {
        component: "custom",
        id: "",
        customComponent({ formik }) {
          return <CheckPrincipalFuldmaegtige formik={formik} />;
        },
      },
    ],
    get: `person/${personId}/principaler/${personPrincipalObjId}`,
    post(values: PersonPrincipalPersonFuldmDTO) {
      const { personPrincipalId, periodeFraDato, periodeTilDatoInklusiv } =
        values;
      return {
        query: `person/${personId}/principaler/`,
        body: {
          personPrincipalId,
          periodeFraDato: dateToyyyyMMdd(periodeFraDato),
          periodeTilDatoInklusiv: periodeTilDatoInklusiv
            ? dateToyyyyMMdd(periodeTilDatoInklusiv)
            : "9999-12-31",
        },
      };
    },
    patch(values: PersonPrincipalPersonFuldmDTO) {
      const ops = getPatchOperations(values);

      return {
        query: `person/${personId}/principaler/${personPrincipalObjId}`,
        body: JSON.stringify(ops),
      };
    },
  };
};

const CheckPrincipalFuldmaegtige = (props: { formik: UseFormikProps }) => {
  const { formik } = props;

  const principalId = formik.values["personPrincipalId"];
  const startDato = formik.values["periodeFraDato"];
  const { data: personFuldmaegtige } = useQuery<FuldmaegtigeForPersonResponse>(
    FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY,
    {
      variables: {
        id: principalId ?? "",
      },
      skip: !principalId,
    },
  );
  const antal = getNumberOfFuldmaegtigeAtDate(personFuldmaegtige, startDato);
  if (antal < 2) return null;
  return (
    <AlertMessage>
      <>
        <b className="fw-semibold">Bemærk:</b> Den valgte advokat er allerede
        principal for <b>{antal}</b> fuldmægtige på starttidspunktet.
      </>
    </AlertMessage>
  );
};
